import { toCsv } from '../tracker/types';
import { PAGES } from './pagePulseConfig';
import {
  CLARITY_INSIGHTS,
  clarityTiles,
  dmTable,
  ga4Duration,
  gaKpiTiles,
  sourceRows,
  total,
  type PageFilter,
} from './pagePulseMetrics';
import type { PulsePayload } from './pagePulseApi';

const labelFor = (page: PageFilter) =>
  page === 'all' ? 'all-3' : (page.split('/').at(-1) ?? 'page');
export const exportKpis = (payload: PulsePayload, page: PageFilter): string[][] => {
  return [
    ['Metric', 'Value'],
    ...gaKpiTiles(payload, page).map((tile) => [tile.label, tile.value]),
  ];
};
/** Same columns, names and order as the on-screen GA4 "Pages and screens" table. */
export const exportPages = (payload: PulsePayload, page: PageFilter): string[][] => [
  [
    'Page path and screen class',
    'Views',
    'Active users',
    'Views per active user',
    'Average engagement time per active user',
    'Event count: form_start',
    'Event count: form_submit',
  ],
  ...PAGES.filter((item) => page === 'all' || item.path === page).map((item) => {
    const views = total(payload, item.path, 'screenPageViews', 'ga4');
    const users = total(payload, item.path, 'activeUsers', 'ga4');
    return [
      item.path,
      String(views),
      String(users),
      users === 0 ? '0.00' : (views / users).toFixed(2),
      ga4Duration(total(payload, item.path, 'userEngagementDuration', 'ga4') / Math.max(users, 1)),
      String(total(payload, item.path, 'form_start', 'ga4')),
      String(total(payload, item.path, 'form_submit', 'ga4')),
    ];
  }),
];
export const exportSources = (payload: PulsePayload, page: PageFilter): string[][] => [
  ['Session source / medium', 'Sessions'],
  ...sourceRows(payload, page).map((row) => [row.label, String(row.sessions)]),
];
export const exportClarity = (payload: PulsePayload, page: PageFilter): string[][] => {
  const sessions = total(payload, page, 'sessions', 'clarity');
  return [
    ['Metric', 'Value', 'Note'],
    ...clarityTiles(payload, page).map((tile) => [tile.label, tile.value, tile.note ?? '']),
    ...CLARITY_INSIGHTS.map(([label, key]) => {
      const value = total(payload, page, key, 'clarity');
      return [
        label,
        String(value),
        `${Math.round((value / Math.max(sessions, 1)) * 100)}% of sessions`,
      ];
    }),
  ];
};
export const exportDm = (payload: PulsePayload, page: PageFilter): string[][] => [
  ['Version', 'Clicks', 'Submits'],
  ...dmTable(payload, page, []).rows.map((row) => [
    row.label,
    String(row.sessions),
    String(row.form_submit),
  ]),
];
export function exportAll(
  payload: PulsePayload,
  page: PageFilter,
): { filename: string; csv: string } {
  const sections = [
    ['Google Analytics KPI'],
    ...exportKpis(payload, page),
    [],
    ['Pages and screens'],
    ...exportPages(payload, page),
    [],
    ['Session source / medium'],
    ...exportSources(payload, page),
    [],
    ['Clarity'],
    ...exportClarity(payload, page),
  ];
  return {
    filename: `page-pulse-${labelFor(page)}-${payload.from}-to-${payload.to}.csv`,
    csv: toCsv(sections),
  };
}
export function downloadPulseCsv(filename: string, rows: string[][]): void {
  downloadPulseText(filename, toCsv(rows));
}
export function downloadPulseText(filename: string, csv: string): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
